import { Controller } from "@hotwired/stimulus"

// Picks the SharePoint site and document library from what the saved credentials can see,
// filling the Site ID and Drive ID fields the form saves. The fields stay editable: the
// picker only types for the operator.
export default class extends Controller {
  static targets = ["siteId", "driveId", "siteChoices", "driveChoices", "message"]

  static values = {
    sitesUrl: String,
    drivesUrl: String,
    sitePlaceholder: String,
    drivePlaceholder: String,
    empty: String,
    requestError: String,
  }

  async loadSites() {
    const sites = await this.fetchJson(this.sitesUrlValue)
    if (!sites) return

    this.renderChoices(
      this.siteChoicesTarget,
      sites.map((site) => ({ value: site.id, label: `${site.name} — ${site.web_url}` })),
      this.siteIdTarget.value,
      this.sitePlaceholderValue,
      "chooseSite",
    )
    if (this.siteIdTarget.value) this.loadDrives()
  }

  chooseSite(event) {
    if (!event.target.value) return

    this.siteIdTarget.value = event.target.value
    this.driveIdTarget.value = ""
    this.siteIdTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.loadDrives()
  }

  async loadDrives() {
    const query = new URLSearchParams({ site_id: this.siteIdTarget.value })
    const drives = await this.fetchJson(`${this.drivesUrlValue}?${query}`)
    if (!drives) return

    this.renderChoices(
      this.driveChoicesTarget,
      drives.map((drive) => ({ value: drive.id, label: drive.name })),
      this.driveIdTarget.value,
      this.drivePlaceholderValue,
      "chooseDrive",
    )
  }

  chooseDrive(event) {
    if (!event.target.value) return

    this.driveIdTarget.value = event.target.value
    this.driveIdTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // A searchable SlimSelect, like every other select in the app (select_controller.js).
  renderChoices(container, options, selected, placeholder, action) {
    if (options.length === 0) {
      container.replaceChildren()
      this.showMessage(this.emptyValue)
      return
    }

    const select = document.createElement("select")
    select.dataset.controller = "select"
    select.dataset.placeholder = placeholder
    select.dataset.action = `change->sharepoint-picker#${action}`
    select.className = "mt-1 block w-full"
    // SlimSelect shows data-placeholder only over a blank option marked as its placeholder
    // (select_controller.js); a bare one renders as an empty, unlabelled box.
    const blank = new Option("", "")
    blank.dataset.placeholder = "true"
    select.append(blank)
    options.forEach(({ value, label }) =>
      select.append(new Option(label, value, false, value === selected)),
    )
    container.replaceChildren(select)
    this.showMessage("")
  }

  async fetchJson(url) {
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      const body = await response.json()
      if (response.ok) return body

      this.showMessage(body.error || this.requestErrorValue)
    } catch (_error) {
      this.showMessage(this.requestErrorValue)
    }
    return null
  }

  showMessage(text) {
    this.messageTarget.textContent = text
    this.messageTarget.classList.toggle("hidden", !text)
  }
}
